import { uploadImageService } from "src/services/uploadImageService";
import { getNormalizedRect, isShapeIntersecting } from "./canvasGeometryUtils";
import { nextTempId, previewRow, previewGrid, snapPointToSeats } from "./canvasSeatGeometryUtils";

function getLocalPointer(stageRef) {
  const stage = stageRef.current;
  const pointer = stage.getPointerPosition();
  const transform = stage.getAbsoluteTransform().copy().invert();
  return transform.point(pointer);
}

// ---------------------------------------------------------------------------
// Select tool: click-drag-release draws a marquee; anything intersecting it
// on release gets selected. A plain click on empty canvas just deselects
// (handled separately, same as the floor plan builder).
// ---------------------------------------------------------------------------

export const handleSelectMouseDown = (e, stageRef, setMarquee) => {
  if (e.evt.button !== 0 || e.target !== stageRef.current) return; // only start on empty canvas
  const point = getLocalPointer(stageRef);
  setMarquee({ x: point.x, y: point.y, width: 0, height: 0 });
};

export const handleSelectMouseMove = (e, stageRef, marquee, setMarquee) => {
  if (!marquee) return;
  const point = getLocalPointer(stageRef);
  setMarquee((prev) => ({ ...prev, width: point.x - prev.x, height: point.y - prev.y }));
};

export const handleSelectMouseUp = (marquee, setMarquee, seats, shapeRefs, setSelectedIds, transformerRef) => {
  if (!marquee) return;
  const box = getNormalizedRect(marquee);
  setMarquee(null);

  if (box.width < 5 || box.height < 5) return; // treat as a click, not a drag

  const selected = seats.filter((seat) => isShapeIntersecting(marquee, seat, shapeRefs)).map((s) => s.id);
  if (!selected.length) return;

  setSelectedIds(selected);
  attachTransformerToIds(selected, seats, shapeRefs, transformerRef);
};

// ---------------------------------------------------------------------------
// Pre-click hover preview, shared by seat/row/grid tools: a ghost seat that
// follows the cursor BEFORE the first click, with the same alignment
// snapping used once a row/grid draft is active — this is what lets you see
// where "seat 1" will land relative to other rows before you commit to it.
// ---------------------------------------------------------------------------

export const handleToolHoverMove = (e, stageRef, existingSeats, setGhostPosition, setHoverGuide) => {
  const raw = getLocalPointer(stageRef);
  const { point, guideX, guideY } = snapPointToSeats(raw, existingSeats);
  setGhostPosition(point);
  setHoverGuide({ guideX, guideY });
};

// ---------------------------------------------------------------------------
// Single-seat tool: a "ghost" seat always tracks the cursor so it's obvious
// the tool is still active; click commits it as a real seat.
// ---------------------------------------------------------------------------

// export const handleSeatToolMouseMove = (e, stageRef, setGhostPosition) => {
//   setGhostPosition(getLocalPointer(stageRef));
// };

// export const handleSeatToolClick = (e, stageRef, sectionId, categoryId, onAddSeat) => {
//   if (e.evt.button !== 0) return;
//   const point = getLocalPointer(stageRef);
//   onAddSeat({
//     id: nextTempId("seat"),
//     sectionId,
//     categoryId: categoryId ?? null,
//     rowLabel: null,
//     label: "1",
//     x: point.x,
//     y: point.y,
//     rotation: 0,
//   });
// };

export const handleSeatToolClick = (e, stageRef, sectionId, categoryId, onAddSeat, existingSeats = []) => {
  if (e.evt.button !== 0) return;
  const raw = getLocalPointer(stageRef);
  const { point } = snapPointToSeats(raw, existingSeats);
  onAddSeat({
    id: nextTempId("seat"),
    sectionId,
    categoryId: categoryId ?? null,
    rowLabel: null,
    label: "1",
    x: point.x,
    y: point.y,
    rotation: 0,
  });
};

// ---------------------------------------------------------------------------
// Row tool: press, drag, release. Shift = hard-snap to 15°, Alt = no snap.
// ---------------------------------------------------------------------------

export const handleRowMouseDown = (e, stageRef, setRowDraft, existingSeats = []) => {
  if (e.evt.button !== 0) return;
  const raw = getLocalPointer(stageRef);
  const { point, guideX, guideY } = snapPointToSeats(raw, existingSeats);
  setRowDraft({ start: point, current: point, guideX, guideY });
};

export const handleRowMouseMove = (e, stageRef, rowDraft, setRowDraft, existingSeats = []) => {
  if (!rowDraft) return;
  const raw = getLocalPointer(stageRef);
  const { point, guideX, guideY } = snapPointToSeats(raw, existingSeats);
  setRowDraft((prev) => ({ ...prev, current: point, shift: e.evt.shiftKey, alt: e.evt.altKey, guideX, guideY }));
};

export const handleRowMouseUp = (rowDraft, setRowDraft, seatSpacing, sectionId, categoryId, onFinalizeRow, startRowLabel) => {
  if (!rowDraft) return;
  setRowDraft(null);
  const { descriptor, seats } = previewRow(rowDraft.start, rowDraft.current, {
    seatSpacing, shift: rowDraft.shift, alt: rowDraft.alt, sectionId, categoryId, startRowLabel
  });
  if (descriptor.seatsPerRow < 2) return;
  onFinalizeRow(descriptor, seats); // seats already carry correct sectionId, top-level AND nested
};

// ---------------------------------------------------------------------------
// Grid tool: press, drag a rectangle, release. Produces rows × columns of
// seats in a single step (replaces the old "draw row, then multiply" flow).
// ---------------------------------------------------------------------------

export const handleGridMouseDown = (e, stageRef, setGridDraft, existingSeats = []) => {
  if (e.evt.button !== 0) return;
  const raw = getLocalPointer(stageRef);
  const { point, guideX, guideY } = snapPointToSeats(raw, existingSeats);
  setGridDraft({ start: point, current: point, guideX, guideY });
};

export const handleGridMouseMove = (e, stageRef, gridDraft, setGridDraft, existingSeats = []) => {
  if (!gridDraft) return;
  const raw = getLocalPointer(stageRef);
  const { point, guideX, guideY } = snapPointToSeats(raw, existingSeats);
  setGridDraft((prev) => ({ ...prev, current: point, guideX, guideY }));
};

export const handleGridMouseUp = (gridDraft, setGridDraft, seatSpacing, rowSpacing, sectionId, categoryId, onFinalizeGrid, startRowLabel) => {
  if (!gridDraft) return;
  setGridDraft(null);
  const { rows, seatsPerRow, rowCount } = previewGrid(gridDraft.start, gridDraft.current, { seatSpacing, rowSpacing, startRowLabel });
  if (seatsPerRow < 2 && rowCount < 2) return;
  onFinalizeGrid(
    rows.map(({ descriptor, seats }) => ({
      descriptor: { ...descriptor, sectionId, categoryId },
      seats: seats.map((s) => ({ ...s, sectionId, categoryId: categoryId ?? s.categoryId })),
    })),
  );
};
// ---------------------------------------------------------------------------
// Decorative rect tool — unchanged behaviour from the legacy floor plan
// builder's draw tool, just re-exported here with matching mousedown wiring
// (the missing mousedown was the bug that made the shape tool inert).
// ---------------------------------------------------------------------------

export const handleDrawRectMouseDown = (e, stageRef, setDrawingRect) => {
  if (e.evt.button !== 0) return;
  const point = getLocalPointer(stageRef);
  setDrawingRect({ x: point.x, y: point.y, width: 0, height: 0 });
};

export const handleDrawRectMouseMove = (e, stageRef, drawingRect, setDrawingRect) => {
  if (!drawingRect) return;
  const point = getLocalPointer(stageRef);
  setDrawingRect((prev) => ({ ...prev, width: point.x - prev.x, height: point.y - prev.y }));
};

export const handleDrawRectMouseUp = (drawingRect, setDrawingRect, onAddRect) => {
  if (!drawingRect) return;
  setDrawingRect(null);
  if (Math.abs(drawingRect.width) < 8 || Math.abs(drawingRect.height) < 8) return;
  onAddRect({
    id: nextTempId("rect"),
    type: "rect",
    ...getNormalizedRect(drawingRect),
    rotation: 0,
  });
};

// ---------------------------------------------------------------------------
// Section tool
// ---------------------------------------------------------------------------

export const handleSectionMouseDown = (e, stageRef, setSectionDraft) => {
  if (e.evt.button !== 0) return;
  const point = getLocalPointer(stageRef);
  setSectionDraft({ x: point.x, y: point.y, width: 0, height: 0 });
};

export const handleSectionMouseMove = (e, stageRef, sectionDraft, setSectionDraft) => {
  if (!sectionDraft) return;
  const point = getLocalPointer(stageRef);
  setSectionDraft((prev) => ({ ...prev, width: point.x - prev.x, height: point.y - prev.y }));
};

export const handleSectionMouseUp = (sectionDraft, setSectionDraft, onFinalizeSection) => {
  if (!sectionDraft) return;
  setSectionDraft(null);
  if (Math.abs(sectionDraft.width) < 20 || Math.abs(sectionDraft.height) < 20) return;
  onFinalizeSection(getNormalizedRect(sectionDraft));
};

// ---------------------------------------------------------------------------
// Text tool — single click drops a text element at the click point.
// ---------------------------------------------------------------------------

export const handleTextToolClick = (e, stageRef, onAddText) => {
  if (e.evt.button !== 0) return;
  const point = getLocalPointer(stageRef);
  onAddText({
    id: nextTempId("text"),
    type: "text",
    x: point.x,
    y: point.y,
    text: "Double click to edit",
    fontSize: 14,
    width: 150,
    height: 40,
    rotation: 0,
  });
};

// ---------------------------------------------------------------------------
// Image tool — this actually reads the file and adds a canvas element now;
// previously nothing was wired to the <input>'s onChange at all.
// ---------------------------------------------------------------------------

export const handleImageFileChange = async (e, enqueueSnackbar, uploadMedia, setMediaPublic, onAddImage) => {
  const file = e.target.files[0];
  if (!file) return;

  const imageUrl = await uploadImageService(file, enqueueSnackbar, uploadMedia, setMediaPublic, false, true);
  if (!imageUrl) return;

  const img = new window.Image();
  img.onload = () => {
    const aspectRatio = img.width / img.height;
    const width = 400 * aspectRatio;
    onAddImage({
      id: nextTempId("image"),
      type: "image",
      src: imageUrl,
      x: 100,
      y: 100,
      width,
      height: 400,
      rotation: 0,
    });
  };
  img.src = imageUrl;

  // allow re-selecting the same file again later
  e.target.value = "";
};

export const attachTransformerToIds = (ids, seats, shapeRefs, transformerRef) => {
  setTimeout(() => {
    // only pull a row's label in when the WHOLE row is selected — a
    // partial or drilled-in selection shouldn't be able to drag/rotate
    // anything, the label included
    const rowGroupIds = new Set(
      ids.map((id) => seats.find((s) => s.id === id)?.metadata?.rowGroup?.rowGroupId).filter(Boolean),
    );
    const fullySelectedGroupIds = [...rowGroupIds].filter((gid) => {
      const groupSeatIds = seats.filter((s) => s.metadata?.rowGroup?.rowGroupId === gid).map((s) => s.id);
      return groupSeatIds.every((id) => ids.includes(id));
    });
    const labelIds = fullySelectedGroupIds.map((gid) => `rowlabel-${gid}`);

    const nodes = [...ids, ...labelIds].map((sid) => shapeRefs.current[sid]).filter(Boolean);
    if (transformerRef.current) {
      transformerRef.current.nodes(nodes);
      transformerRef.current.getLayer().batchDraw();
    }
  }, 0);
};